import React, { useState, useEffect } from 'react';
import './AccreditationBank.css';
import { getAllTemplates, deleteTemplate, updateTemplateName } from '../services/api';
import { scrapeAndExtractLearningGoals } from '../services/crawlApi';
import { useNotification, NotificationContainer } from './Notification';
import * as XLSX from 'xlsx';
import { useLocation } from 'react-router-dom';
// check
// Import API_BASE_URL
const API_BASE_URL = '/api';

console.log("process.env.REACT_APP_API_URL", process.env.REACT_APP_API_URL);

// Add the generateKeywordsAndConcepts API function
const generateKeywordsAndConcepts = async (templateId, school) => {
  try {
    const response = await fetch(`${API_BASE_URL}/generate-keywords-and-concepts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_id: Number(templateId),
        school: school
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error generating keywords and concepts:', error);
    throw error;
  }
};

// Add the updateTemplateContent API function
const updateTemplateContent = async (templateType, learningGoalId, updatePayload) => {
  try {
    const requestBody = {
      template_type: templateType,
      learning_goal_id: Number(learningGoalId),
      ...updatePayload // Spread only the changed fields
    };
    
    const response = await fetch(`${API_BASE_URL}/update-template-content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating template content:', error);
    throw error;
  }
};

const AccreditationBank = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('view'); // 'view' or 'upload'
  const [expandedTemplates, setExpandedTemplates] = useState({}); // Track expanded templates
  const [expandedLGs, setExpandedLGs] = useState({}); // Track expanded main LGs
  const [expandedSubLGs, setExpandedSubLGs] = useState({}); // Track expanded sub LGs
  const [allExpanded, setAllExpanded] = useState(false); // Track if all templates are expanded
  const [editingTemplate, setEditingTemplate] = useState(null); // Track which template is being edited
  const [newTemplateName, setNewTemplateName] = useState(''); // New name for the template being edited
  const [isUpdatingName, setIsUpdatingName] = useState(false); // Loading state for name update

  // New state variables for editing LG content
  const [editingLG, setEditingLG] = useState(null); // Format: {templateId, lgIndex, isSubLG, subIndex}
  const [editDescription, setEditDescription] = useState('');
  const [editKeywords, setEditKeywords] = useState('');
  const [editConcepts, setEditConcepts] = useState('');
  const [isUpdatingContent, setIsUpdatingContent] = useState(false);
  
  // New state variables for grid-based keyword editing
  const [keywordBlocks, setKeywordBlocks] = useState([]);

  const location = useLocation();
  
  // New state variables for concept box editing
  const [conceptBoxes, setConceptBoxes] = useState([]);

  // Add state variables to track original data for comparison
  const [originalData, setOriginalData] = useState({
    description: '',
    keywords: [],
    concepts: []
  });

  // New template form state
  const [learningGoals, setLearningGoals] = useState([
    { id: 'LG1', type: 'main', description: '' }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Restore the templateName state for uploading new templates
  const [templateName, setTemplateName] = useState('');

  // Add state to track templates being generated
  const [generatingTemplates, setGeneratingTemplates] = useState({});

  // Add notification system
  const { notifications, addNotification, removeNotification } = useNotification();

  // Add state for Excel template functionality
  const [createMethod, setCreateMethod] = useState('manual'); // 'manual', 'excel', 'url'
  const [excelFile, setExcelFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [showExcelPreview, setShowExcelPreview] = useState(false);
  const [isParsingExcel, setIsParsingExcel] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Add state variables for URL crawling
  const [crawlUrl, setCrawlUrl] = useState('');
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlProgress, setCrawlProgress] = useState({ step: 0 });
  const [crawlProgressPercentage, setCrawlProgressPercentage] = useState(0);
  const [crawlError, setCrawlError] = useState('');
  const [crawlSuccess, setCrawlSuccess] = useState(false);

  // Add state variables for multiple frameworks handling
  const [extractedFrameworks, setExtractedFrameworks] = useState([]);
  const [showFrameworkSelection, setShowFrameworkSelection] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [frameworkNames, setFrameworkNames] = useState({});
  const [expandedFrameworkPreviews, setExpandedFrameworkPreviews] = useState({});
  const [currentPreviewFramework, setCurrentPreviewFramework] = useState(null);
  const [isFrameworkSwitchingMode, setIsFrameworkSwitchingMode] = useState(false);
  const [isUrlFrameworkCreated, setIsUrlFrameworkCreated] = useState(false);
  const [createdFrameworks, setCreatedFrameworks] = useState(new Set()); // Store editable names for frameworks
  const getSchoolFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('school') || 'MARYLAND';
  };
  const [school, setSchool] = useState(getSchoolFromUrl());

  // Add state variables for hover-based sub-learning goal addition
  const [hoveredLearningGoal, setHoveredLearningGoal] = useState(null);
  const [showAddSubLG, setShowAddSubLG] = useState(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';
      const data = await getAllTemplates(school);
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplate = (templateId) => {
    setExpandedTemplates(prev => {
      const newState = {
        ...prev,
        [templateId]: !prev[templateId]
      };
      
      // Update allExpanded state based on whether all templates are now expanded
      const allTemplatesExpanded = templates.every(template => 
        newState[template.id]
      );
      setAllExpanded(allTemplatesExpanded);
      
      return newState;
    });
  };

  const handleDeleteTemplate = (e, templateId) => {
    // Stop event propagation to prevent the card from expanding/collapsing
    e.stopPropagation();
    
    // Set the template to delete
    setTemplateToDelete(templateId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    
      setLoading(true);
    try {
      const response = await deleteTemplate(templateToDelete);
      //console.log(`Template ${templateToDelete} deleted successfully:`, response);
      
          // Remove the deleted template from the list
      setTemplates(templates.filter(template => template.id !== templateToDelete));
          
          setSuccessMessage('Template deleted successfully');
          // Clear success message after 3 seconds
          setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error(`Error deleting template ${templateToDelete}:`, err);
          setError(err.message || 'Failed to delete template');
          // Clear error message after 5 seconds
          setTimeout(() => setError(null), 5000);
    } finally {
          setLoading(false);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const toggleAllTemplates = () => {
    const newExpandedState = !allExpanded;
    setAllExpanded(newExpandedState);
    
    // Create an object with all templates either expanded or collapsed
    const newExpandedTemplates = {};
    templates.forEach(template => {
      newExpandedTemplates[template.id] = newExpandedState;
    });
    
    setExpandedTemplates(newExpandedTemplates);
  };

  // Toggle expansion of main learning goal
  const toggleLG = (templateId, lgIndex) => {
    setExpandedLGs(prev => ({
      ...prev,
      [templateId + '-' + lgIndex]: !prev[templateId + '-' + lgIndex]
    }));
  };

  // Toggle expansion of sub learning goal
  const toggleSubLG = (templateId, lgIndex, objIndex) => {
    setExpandedSubLGs(prev => ({
      ...prev,
      [templateId + '-' + lgIndex + '-' + objIndex]: !prev[templateId + '-' + lgIndex + '-' + objIndex]
    }));
  };

  const handleAddLearningGoal = (type) => {
    const newLearningGoals = [...learningGoals];
    
    if (type === 'main') {
      // Find the highest main LG number
      const mainLGs = learningGoals.filter(lg => lg.type === 'main');
      const highestNumber = mainLGs.length > 0 
        ? Math.max(...mainLGs.map(lg => parseInt(lg.id.replace('LG', '')))) 
        : 0;
      
      newLearningGoals.push({
        id: `LG${highestNumber + 1}`,
        type: 'main',
        description: ''
      });
    } else {
      // Find the last main LG
      const mainLGs = learningGoals.filter(lg => lg.type === 'main');
      if (mainLGs.length === 0) {
        addNotification('Please add a main learning goal first', 'warning');
        return;
      }
      
      const lastMainLG = mainLGs[mainLGs.length - 1];
      const mainLGNumber = lastMainLG.id.replace('LG', '');
      
      // Find the highest sub-LG number for this main LG
      const subLGs = learningGoals.filter(lg => 
        lg.type === 'sub' && lg.id.startsWith(`LG${mainLGNumber}.`)
      );
      
      const highestSubNumber = subLGs.length > 0
        ? Math.max(...subLGs.map(lg => parseInt(lg.id.split('.')[1])))
        : 0;
      
      newLearningGoals.push({
        id: `LG${mainLGNumber}.${highestSubNumber + 1}`,
        type: 'sub',
        description: ''
      });
    }
    
    setLearningGoals(newLearningGoals);
  };

  // New function to add sub-learning goal to a specific main learning goal
  const handleAddSubLearningGoal = (mainLGIndex) => {
    const mainLG = learningGoals[mainLGIndex];
    if (mainLG.type !== 'main') return;
    
    const mainLGNumber = mainLG.id.replace('LG', '');
    
    // Find the highest sub-LG number for this specific main LG
    const subLGs = learningGoals.filter(lg => 
      lg.type === 'sub' && lg.id.startsWith(`LG${mainLGNumber}.`)
    );
    
    const highestSubNumber = subLGs.length > 0
      ? Math.max(...subLGs.map(lg => parseInt(lg.id.split('.')[1])))
      : 0;
    
    const newSubLG = {
      id: `LG${mainLGNumber}.${highestSubNumber + 1}`,
      type: 'sub',
      description: ''
    };
    
    // Insert the new sub-LG after the main LG and its existing sub-LGs
    const newLearningGoals = [...learningGoals];
    let insertIndex = mainLGIndex + 1;
    
    // Find the correct position to insert (after all existing sub-LGs of this main LG)
    for (let i = mainLGIndex + 1; i < newLearningGoals.length; i++) {
      if (newLearningGoals[i].type === 'sub' && newLearningGoals[i].id.startsWith(`LG${mainLGNumber}.`)) {
        insertIndex = i + 1;
      } else {
        break;
      }
    }
    
    newLearningGoals.splice(insertIndex, 0, newSubLG);
    setLearningGoals(newLearningGoals);
    setShowAddSubLG(null); // Hide the add sub-LG option
  };

  const handleRemoveLearningGoal = (index) => {
    const goalToRemove = learningGoals[index];
    
    // Don't allow removing the first main LG if it's the only one and empty
    if (index === 0 && learningGoals.length === 1 && !goalToRemove.description.trim()) {
      return;
    }
    
    // If it's a main LG, also remove all its sub-LGs
    if (goalToRemove.type === 'main') {
      const mainLGNumber = goalToRemove.id.replace('LG', '');
      let newLearningGoals = learningGoals.filter(lg => 
        !(lg.id === goalToRemove.id || (lg.type === 'sub' && lg.id.startsWith(`LG${mainLGNumber}.`)))
      );
      
      // Renumber all learning goals
      newLearningGoals = renumberLearningGoals(newLearningGoals);
      setLearningGoals(newLearningGoals);
    } else {
      // Just remove the sub-LG
      const newLearningGoals = [...learningGoals];
      newLearningGoals.splice(index, 1);
      
      // Renumber all learning goals
      const renumberedGoals = renumberLearningGoals(newLearningGoals);
      setLearningGoals(renumberedGoals);
    }
  };

  // Helper function to renumber learning goals
  const renumberLearningGoals = (goals) => {
    let mainLGCounter = 1;
    const renumbered = [];
    
    for (let i = 0; i < goals.length; i++) {
      const lg = { ...goals[i] };
      
      if (lg.type === 'main') {
        lg.id = `LG${mainLGCounter}`;
        renumbered.push(lg);
        
        // Find and renumber all sub-LGs for this main LG
        let subCounter = 1;
        for (let j = i + 1; j < goals.length; j++) {
          if (goals[j].type === 'sub') {
            const subLG = { ...goals[j] };
            subLG.id = `LG${mainLGCounter}.${subCounter}`;
            renumbered.push(subLG);
            subCounter++;
            i = j; // Skip these in the outer loop
          } else {
            break; // Stop when we hit the next main LG
          }
        }
        
        mainLGCounter++;
      }
    }
    
    return renumbered;
  };

  const handleLearningGoalChange = (index, value) => {
    const newLearningGoals = [...learningGoals];
    newLearningGoals[index].description = value;
    setLearningGoals(newLearningGoals);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // Get school parameter from URL
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND'; // Default to MARYLAND if not found
      
      // Format the learning goals into the required structure
      const formattedLGs = {};
      learningGoals.forEach(lg => {
        if (lg.description.trim()) {
          formattedLGs[lg.id] = lg.description;
        }
      });
      
      // Prepare the request body
      const requestBody = {
        school,
        template_name: templateName,
        LG: formattedLGs
      };
      
      const response = await fetch(`${API_BASE_URL}/upload-new-learning-goals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload template');
      }
      
      // Check if this was created from URL extraction
      if (createMethod === 'url' && (crawlSuccess || isFrameworkSwitchingMode)) {
        // For URL-based creation, don't reset form or redirect
        // Just mark as successfully created and show success
        setSuccessMessage('Framework created successfully from URL extraction!');
        // Keep switching mode active so user can create other frameworks
        // setIsFrameworkSwitchingMode(false); // Keep this true to allow switching
        setShowFrameworkSelection(false);
        setCrawlSuccess(true);
        setIsUrlFrameworkCreated(true);
        // Track which framework was created
        setCreatedFrameworks(prev => new Set([...prev, currentPreviewFramework]));
        // Keep the form data intact to show the created framework
      } else {
        // For manual/excel creation, reset form and switch to view tab
        setTemplateName('');
        setLearningGoals([{ id: 'LG1', type: 'main', description: '' }]);
        setSuccessMessage('Template uploaded successfully!');
        
        // Switch back to the View Frameworks tab
        setActiveTab('view');
      }
      
      // Refresh the templates list
      fetchTemplates();
    } catch (err) {
      console.error('Error uploading template:', err);
      setError(err.message || 'Failed to upload template');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle starting template name edit
  const startEditingTemplateName = (e, template) => {
    e.stopPropagation(); // Prevent template expansion
    setEditingTemplate(template.id);
    setNewTemplateName(template.name);
  };

  // Function to cancel editing
  const cancelEditingTemplateName = (e) => {
    e.stopPropagation(); // Prevent template expansion
    setEditingTemplate(null);
    setNewTemplateName('');
  };

  // Function to save template name
  const saveTemplateName = async (e, templateId) => {
    e.stopPropagation(); // Prevent template expansion
    
    if (!newTemplateName.trim()) {
      return; // Don't save empty names
    }
    
    setIsUpdatingName(true);
    setError(null);
    
    try {
      // Use the API service function instead of direct fetch
      await updateTemplateName(templateId, newTemplateName.trim());
      
      // Update the template name in local state
      setTemplates(templates.map(template => 
        template.id === templateId 
          ? { ...template, name: newTemplateName.trim() } 
          : template
      ));
      
      // Show success message
      setSuccessMessage('Framework name updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Reset editing state
      setEditingTemplate(null);
      setNewTemplateName('');
    } catch (err) {
      console.error('Error updating framework name:', err);
      setError(err.message || 'Failed to update framework name');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsUpdatingName(false);
    }
  };

  // Function to start editing learning goal content
  const startEditingLG = (e, templateId, lgIndex, isSubLG = false, subIndex = null) => {
    e.stopPropagation(); // Prevent template expansion
    
    // Find the learning goal data
    const template = templates.find(t => t.id === templateId);
    if (!template || !template.LG) return;
    
    let description = '';
    let keywords = [];
    let concepts = [];
    
    if (isSubLG && subIndex !== null) {
      // Get sub learning goal data
      const subLG = template.LG[lgIndex]?.objectives?.[subIndex];
      if (subLG) {
        description = subLG.description || '';
        keywords = subLG.keywords || [];
        concepts = subLG.concepts || [];
      }
    } else {
      // Get main learning goal data
      const mainLG = template.LG[lgIndex];
      if (mainLG) {
        description = mainLG.description || '';
        keywords = mainLG.keywords || [];
        concepts = mainLG.concepts || [];
      }
    }
    
    // Store original data for comparison
    setOriginalData({
      description: description,
      keywords: Array.isArray(keywords) ? [...keywords] : [],
      concepts: Array.isArray(concepts) ? [...concepts] : []
    });
    
    // Set editing state
    setEditingLG({
      templateId,
      lgIndex,
      isSubLG,
      subIndex
    });
    
    setEditDescription(description);
    setEditKeywords(Array.isArray(keywords) ? keywords.join(', ') : '');
    setKeywordBlocks(Array.isArray(keywords) ? keywords.map(k => k.trim()).filter(k => k.length > 0) : []);
    
    setEditConcepts(Array.isArray(concepts) ? concepts.join('\n') : '');
    setConceptBoxes(Array.isArray(concepts) ? concepts.map(c => c.trim()).filter(c => c.length > 0) : []);
    
    // Ensure the section is expanded
    if (!expandedTemplates[templateId]) {
      toggleTemplate(templateId);
    }
    
    if (!expandedLGs[templateId + '-' + lgIndex]) {
      toggleLG(templateId, lgIndex);
    }
    
    if (isSubLG && !expandedSubLGs[templateId + '-' + lgIndex + '-' + subIndex]) {
      toggleSubLG(templateId, lgIndex, subIndex);
    }
  };
  
  // Function to cancel editing learning goal
  const cancelEditingLG = (e) => {
    e.stopPropagation(); // Prevent template expansion
    setEditingLG(null);
    setEditDescription('');
    setEditKeywords('');
    setEditConcepts('');
    setKeywordBlocks([]);
    setConceptBoxes([]);
    setOriginalData({ description: '', keywords: [], concepts: [] });
  };
  
  // Add a keyword to the grid
  const addKeywordBlock = () => {
    setKeywordBlocks([...keywordBlocks, '']);
  };
  
  // Update a keyword in the grid
  const updateKeywordBlock = (index, value) => {
    const newKeywords = [...keywordBlocks];
    newKeywords[index] = value;
    setKeywordBlocks(newKeywords);
    
    // Also update the comma-separated string for API compatibility
    setEditKeywords(newKeywords.join(', '));
  };
  
  // Remove a keyword from the grid
  const removeKeywordBlock = (index) => {
    const newKeywords = [...keywordBlocks];
    newKeywords.splice(index, 1);
    setKeywordBlocks(newKeywords);
    
    // Also update the comma-separated string for API compatibility
    setEditKeywords(newKeywords.join(', '));
  };
  
  // Add a concept box
  const addConceptBox = () => {
    setConceptBoxes([...conceptBoxes, '']);
  };
  
  // Update a concept in its box
  const updateConceptBox = (index, value) => {
    const newConcepts = [...conceptBoxes];
    newConcepts[index] = value;
    setConceptBoxes(newConcepts);
    
    // Also update the newline-separated string for API compatibility
    setEditConcepts(newConcepts.join('\n'));
  };
  
  // Remove a concept box
  const removeConceptBox = (index) => {
    const newConcepts = [...conceptBoxes];
    newConcepts.splice(index, 1);
    setConceptBoxes(newConcepts);
    
    // Also update the newline-separated string for API compatibility
    setEditConcepts(newConcepts.join('\n'));
  };
  
  // Function to save learning goal content
  const saveLGContent = async (e) => {
    e.stopPropagation(); // Prevent template expansion
    
    if (!editingLG) return;
    
    setIsUpdatingContent(true);
    setError(null);
    
    try {
      const { templateId, lgIndex, isSubLG, subIndex } = editingLG;
      
      // Find the template and learning goal
      const template = templates.find(t => t.id === templateId);
      if (!template || !template.LG) throw new Error('Template not found');
      
      let learningGoalId;
      let templateType;
      
      if (isSubLG && subIndex !== null) {
        // Sub learning goal
        const subLG = template.LG[lgIndex]?.objectives?.[subIndex];
        if (!subLG) throw new Error('Sub-learning goal not found');
        
        // Use the actual sub-learning goal ID
        learningGoalId = subLG.sub_learning_goal_id;
        templateType = 'sub_template';
      } else {
        // Main learning goal
        const mainLG = template.LG[lgIndex];
        if (!mainLG) throw new Error('Learning goal not found');
        
        // Use the actual learning goal ID from the database
        learningGoalId = mainLG.id;
        templateType = 'parent';
      }
      
      // Prepare current data
      const currentDescription = editDescription.trim();
      const currentKeywords = keywordBlocks.filter(k => k.trim().length > 0);
      const currentConcepts = conceptBoxes.filter(c => c.trim().length > 0);
      
      // Helper function to compare arrays (sorted)
      const arraysEqual = (arr1, arr2) => {
        if (arr1.length !== arr2.length) return false;
        const sorted1 = [...arr1].sort();
        const sorted2 = [...arr2].sort();
        return sorted1.every((val, index) => val === sorted2[index]);
      };
      
      // Build payload with only changed fields
      const updatePayload = {};
      
      // Check if description changed
      if (currentDescription !== originalData.description) {
        updatePayload.description = currentDescription;
      }
      
      // Check if keywords changed
      if (!arraysEqual(currentKeywords, originalData.keywords)) {
        updatePayload.keywords = currentKeywords;
      }
      
      // Check if concepts changed
      if (!arraysEqual(currentConcepts, originalData.concepts)) {
        updatePayload.concepts = currentConcepts;
      }
      
      // Only make API call if there are changes
      if (Object.keys(updatePayload).length > 0) {
        // Call API to update content with only changed fields
      await updateTemplateContent(
        templateType,
        learningGoalId,
          updatePayload
      );
      
        // Update local state with current data
      const updatedTemplates = [...templates];
      const templateIndex = updatedTemplates.findIndex(t => t.id === templateId);
      
      if (templateIndex !== -1) {
        if (isSubLG && subIndex !== null) {
          // Update sub learning goal
          if (updatedTemplates[templateIndex].LG[lgIndex]?.objectives?.[subIndex]) {
              if (updatePayload.description !== undefined) {
                updatedTemplates[templateIndex].LG[lgIndex].objectives[subIndex].description = currentDescription;
              }
              if (updatePayload.keywords !== undefined) {
                updatedTemplates[templateIndex].LG[lgIndex].objectives[subIndex].keywords = currentKeywords;
              }
              if (updatePayload.concepts !== undefined) {
                updatedTemplates[templateIndex].LG[lgIndex].objectives[subIndex].concepts = currentConcepts;
              }
          }
        } else {
          // Update main learning goal
          if (updatedTemplates[templateIndex].LG[lgIndex]) {
              if (updatePayload.description !== undefined) {
                updatedTemplates[templateIndex].LG[lgIndex].description = currentDescription;
              }
              if (updatePayload.keywords !== undefined) {
                updatedTemplates[templateIndex].LG[lgIndex].keywords = currentKeywords;
              }
              if (updatePayload.concepts !== undefined) {
                updatedTemplates[templateIndex].LG[lgIndex].concepts = currentConcepts;
              }
          }
        }
        
        setTemplates(updatedTemplates);
      }
      
      // Show success message
      setSuccessMessage('Learning goal updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        // Show message that no changes were detected
        setSuccessMessage('No changes detected');
        setTimeout(() => setSuccessMessage(''), 2000);
      }
      
      // Reset editing state
      setEditingLG(null);
      setEditDescription('');
      setEditKeywords('');
      setEditConcepts('');
      setKeywordBlocks([]);
      setConceptBoxes([]);
      setOriginalData({ description: '', keywords: [], concepts: [] });
      
    } catch (err) {
      console.error('Error updating learning goal:', err);
      setError(err.message || 'Failed to update learning goal');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsUpdatingContent(false);
    }
  };

  // Function to check if a template has keywords and concepts generated
  const hasGeneratedContent = (template) => {
    if (!template.LG || template.LG.length === 0) return false;
    
    // Check if any learning goal or sub-learning goal has keywords or concepts
    return template.LG.some(lg => {
      // Check main learning goal
      const hasMainContent = (lg.keywords && lg.keywords.length > 0) || 
                            (lg.concepts && lg.concepts.length > 0);
      
      // Check sub learning goals
      const hasSubContent = lg.objectives && lg.objectives.some(obj => 
        (obj.keywords && obj.keywords.length > 0) || 
        (obj.concepts && obj.concepts.length > 0)
      );
      
      return hasMainContent || hasSubContent;
    });
  };

  // Function to handle generate keywords and concepts
  const handleGenerateKeywords = async (e, templateId, school) => {
    e.stopPropagation();
    
    setGeneratingTemplates(prev => ({ ...prev, [templateId]: true }));
    
    try {
      const result = await generateKeywordsAndConcepts(templateId, school);
      //console.log('Generate keywords result:', result);
      
      // Refresh templates after generation
      await fetchTemplates();
      
      addNotification('Keywords and concepts generated successfully!', 'success');
    } catch (error) {
      console.error('Error generating keywords:', error);
      addNotification('Failed to generate keywords and concepts. Please try again.', 'error');
    } finally {
      setGeneratingTemplates(prev => ({ ...prev, [templateId]: false }));
    }
  };

  // CSV Template Functions
  const handleDownloadTemplate = () => {
    // Create Excel template with new structure
    const templateData = [
      ['Instructions:', 'This template shows the expected format for learning goals'],
      ['Row 1-3:', 'These rows will be ignored during import'],
      ['Starting from Row 4:', 'First column = Main Learning Goal, Columns 2-n = Sub Learning Goals'],
      ['Main Learning Goal 1', 'Sub Learning Goal 1.1', 'Sub Learning Goal 1.2', 'Sub Learning Goal 1.3', 'Sub Learning Goal 1.4'],
      ['Main Learning Goal 2', 'Sub Learning Goal 2.1', 'Sub Learning Goal 2.2'],
      ['Main Learning Goal 3', 'Sub Learning Goal 3.1', 'Sub Learning Goal 3.2', 'Sub Learning Goal 3.3']
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(templateData);

    // Set column widths
    worksheet['!cols'] = [
      { width: 30 }, // Main Learning Goal column
      { width: 25 }, // Sub Learning Goal columns
      { width: 25 },
      { width: 25 },
      { width: 25 }
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Learning Goals Template');

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, 'learning_goals_template.xlsx');
  };

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setExcelFile(file);
      parseExcelFile(file);
    } else {
      addNotification('Please select a valid Excel file.', 'warning');
    }
  };

  // Add drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        setExcelFile(file);
        parseExcelFile(file);
      } else {
        addNotification('Please drop a valid Excel file.', 'warning');
      }
    }
  };

  const parseExcelFile = (file) => {
    setIsParsingExcel(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const excel = e.target.result;
        const workbook = XLSX.read(excel, { type: 'buffer' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        
        // Convert sheet to array of arrays to handle specific rows and columns
        const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Skip first 3 rows, start from row 4 (index 3)
        if (rawData.length < 4) {
          addNotification('Excel file must contain at least 4 rows (starting from row 4).', 'error');
          setIsParsingExcel(false);
          return;
        }

        const dataRows = rawData.slice(3); // Skip first 3 rows
        const parsedData = [];

        dataRows.forEach((row, rowIndex) => {
          if (!row || row.length === 0) return; // Skip empty rows
          
          const mainLearningGoalText = row[0] ? String(row[0]).trim() : '';
          
          if (mainLearningGoalText) { // If main learning goal exists
            const currentMainLGNumber = parsedData.filter(lg => lg.type === 'main').length + 1;
            const mainLearningGoal = {
              type: 'main',
              description: mainLearningGoalText,
              id: `LG${currentMainLGNumber}`
            };
            parsedData.push(mainLearningGoal);

            // Add sub learning goals (columns 2 to n)
            for (let i = 1; i < row.length; i++) {
              const subLearningGoalText = row[i] ? String(row[i]).trim() : '';
              if (subLearningGoalText && subLearningGoalText !== '') {
                const subLearningGoal = {
                  type: 'sub',
                  description: subLearningGoalText,
                  id: `LG${currentMainLGNumber}.${i}`
                };
                parsedData.push(subLearningGoal);
              }
            }
          }
        });

        if (parsedData.length === 0) {
          addNotification('No valid learning goals found in Excel file.', 'error');
        } else {
          setExcelData(parsedData);
          setShowExcelPreview(true);
          addNotification(`Successfully parsed ${parsedData.length} learning goals from Excel.`, 'success');
        }
      } catch (error) {
        addNotification('Error parsing Excel file. Please check the format.', 'error');
      } finally {
        setIsParsingExcel(false);
      }
    };

    reader.onerror = () => {
      addNotification('Error reading Excel file.', 'error');
      setIsParsingExcel(false);
    };

    reader.readAsArrayBuffer(file);
  };

  // CSV helper functions for editing
  const handleExcelLearningGoalChange = (index, value) => {
    const newExcelData = [...excelData];
    newExcelData[index].description = value;
    setExcelData(newExcelData);
  };

  const handleRemoveExcelLearningGoal = (index) => {
    const goalToRemove = excelData[index];
    
    // Don't allow removing the first main LG if it's the only one and empty
    if (index === 0 && excelData.length === 1 && !goalToRemove.description.trim()) {
      return;
    }
    
    // If it's a main LG, also remove all its sub-LGs
    if (goalToRemove.type === 'main') {
      const mainLGNumber = goalToRemove.id.replace('LG', '');
      let newExcelData = excelData.filter(lg => 
        !(lg.id === goalToRemove.id || (lg.type === 'sub' && lg.id.startsWith(`LG${mainLGNumber}.`)))
      );
      
      // Renumber all learning goals
      newExcelData = renumberExcelLearningGoals(newExcelData);
      setExcelData(newExcelData);
    } else {
      // Just remove the sub-LG
      const newExcelData = [...excelData];
      newExcelData.splice(index, 1);
      
      // Renumber all learning goals
      const renumberedGoals = renumberExcelLearningGoals(newExcelData);
      setExcelData(renumberedGoals);
    }
  };

  const handleAddExcelLearningGoal = (type) => {
    const newExcelData = [...excelData];
    
    if (type === 'main') {
      // Find the highest main LG number
      const mainLGs = excelData.filter(lg => lg.type === 'main');
      const highestNumber = mainLGs.length > 0 
        ? Math.max(...mainLGs.map(lg => parseInt(lg.id.replace('LG', '')))) 
        : 0;
      
      newExcelData.push({
        id: `LG${highestNumber + 1}`,
        type: 'main',
        description: ''
      });
    }
    
    setExcelData(newExcelData);
  };

  const handleAddExcelSubLearningGoal = (mainLGIndex) => {
    const mainLG = excelData[mainLGIndex];
    if (mainLG.type !== 'main') return;
    
    const mainLGNumber = mainLG.id.replace('LG', '');
    
    // Find the highest sub-LG number for this specific main LG
    const subLGs = excelData.filter(lg => 
      lg.type === 'sub' && lg.id.startsWith(`LG${mainLGNumber}.`)
    );
    
    const highestSubNumber = subLGs.length > 0
      ? Math.max(...subLGs.map(lg => parseInt(lg.id.split('.')[1])))
      : 0;
    
    const newSubLG = {
      id: `LG${mainLGNumber}.${highestSubNumber + 1}`,
      type: 'sub',
      description: ''
    };
    
    // Insert the new sub-LG after the main LG and its existing sub-LGs
    const newExcelData = [...excelData];
    let insertIndex = mainLGIndex + 1;
    
    // Find the correct position to insert (after all existing sub-LGs of this main LG)
    for (let i = mainLGIndex + 1; i < newExcelData.length; i++) {
      if (newExcelData[i].type === 'sub' && newExcelData[i].id.startsWith(`LG${mainLGNumber}.`)) {
        insertIndex = i + 1;
      } else {
        break;
      }
    }
    
    newExcelData.splice(insertIndex, 0, newSubLG);
    setExcelData(newExcelData);
  };

  // Helper function to renumber Excel learning goals
  const renumberExcelLearningGoals = (goals) => {
    let mainLGCounter = 1;
    const renumbered = [];
    
    for (let i = 0; i < goals.length; i++) {
      const lg = { ...goals[i] };
      
      if (lg.type === 'main') {
        lg.id = `LG${mainLGCounter}`;
        renumbered.push(lg);
        
        // Find and renumber all sub-LGs for this main LG
        let subCounter = 1;
        for (let j = i + 1; j < goals.length; j++) {
          if (goals[j].type === 'sub') {
            const subLG = { ...goals[j] };
            subLG.id = `LG${mainLGCounter}.${subCounter}`;
            renumbered.push(subLG);
            subCounter++;
            i = j; // Skip these in the outer loop
          } else {
            break; // Stop when we hit the next main LG
          }
        }
        
        mainLGCounter++;
      }
    }
    
    return renumbered;
  };

  const handleConfirmExcelData = () => {
    setLearningGoals(excelData);
    setShowExcelPreview(false);
    setCreateMethod('manual'); // Switch to manual mode for editing
    addNotification('Excel data loaded successfully! You can now edit and confirm.', 'success');
  };

  const handleCancelExcelUpload = () => {
    setExcelFile(null);
    setExcelData([]);
    setShowExcelPreview(false);
  };

  // URL Crawling Functions
  const handleUrlCrawl = async () => {
    if (!crawlUrl.trim()) {
      addNotification('Please enter a valid URL', 'warning');
      return;
    }

    setIsCrawling(true);
    setCrawlError('');
    setCrawlSuccess(false);
    setCrawlProgress({ step: 0 });
    setCrawlProgressPercentage(0);
    setExtractedFrameworks([]);
    setShowFrameworkSelection(false);
    setSelectedFramework(null);

    // Start the percentage animation - longer duration for two-step process
    const animationInterval = setInterval(() => {
      setCrawlProgressPercentage(prev => {
        const newPercentage = prev + (100 / 600); // 600 increments over 60 seconds (100ms intervals)
        return newPercentage >= 100 ? 100 : newPercentage;
      });
    }, 100); // Update every 100ms

    try {
      // Call the API function with URL (two-step process: initiate -> poll)
      const extractedData = await scrapeAndExtractLearningGoals(crawlUrl, (step) => {
        setCrawlProgress({ step });
      });
      
      if (extractedData.frameworks && extractedData.frameworks.length > 0) {
        setExtractedFrameworks(extractedData.frameworks);
        
        // Initialize framework names with original names (editable)
        const initialNames = {};
        extractedData.frameworks.forEach((framework, index) => {
          initialNames[index] = framework.name || `Framework ${index + 1}`;
        });
        setFrameworkNames(initialNames);
        
        setCrawlSuccess(true);
        setCrawlProgressPercentage(100);
        
        if (extractedData.frameworks.length === 1) {
          // If only one framework, auto-select it
          handleFrameworkSelect(0, extractedData.frameworks);
          addNotification('Framework extracted successfully!', 'success');
        } else {
          // Multiple frameworks - automatically start preview mode with first framework
          setShowFrameworkSelection(false);
          setIsFrameworkSwitchingMode(true);
          handleSwitchToFramework(0, extractedData.frameworks);
          addNotification(`${extractedData.frameworks.length} frameworks extracted. Use the navigation above to switch between them.`, 'info');
        }
      } else {
        throw new Error('No frameworks found in the extracted data.');
      }
      
    } catch (error) {
      console.error('Error extracting frameworks from URL:', error);
      setCrawlError(error.message || 'Failed to extract frameworks. Please check the URL and try again.');
      addNotification('Failed to extract frameworks', 'error');
    } finally {
      clearInterval(animationInterval); // Clear the animation interval
      setIsCrawling(false);
      setCrawlProgress({ step: 0 });
      setCrawlProgressPercentage(0);
    }
  };

  const resetCrawlState = () => {
    setCrawlUrl('');
    setCrawlError('');
    setCrawlSuccess(false);
    setCrawlProgress({ step: 0 });
    setCrawlProgressPercentage(0);
    setExtractedFrameworks([]);
    setShowFrameworkSelection(false);
    setSelectedFramework(null);
    setFrameworkNames({});
    setExpandedFrameworkPreviews({});
    setCurrentPreviewFramework(null);
    setIsFrameworkSwitchingMode(false);
    setIsUrlFrameworkCreated(false);
    setCreatedFrameworks(new Set());
  };

  // Handle framework selection
  const handleFrameworkSelect = (frameworkIndex, frameworksArray = null) => {
    // Use provided frameworks array or fall back to state
    const frameworks = frameworksArray || extractedFrameworks;
    
    const selectedFramework = frameworks[frameworkIndex];
    if (!selectedFramework) return;
    
    // Transform the selected framework's learning goals into the expected format
    const formattedLearningGoals = selectedFramework.learningGoals.map((lg, index) => ({
      id: lg.id || `LG${index + 1}`,
      type: lg.type || 'main',
      description: lg.description
    }));
    
    setLearningGoals(formattedLearningGoals);
    setSelectedFramework(frameworkIndex);
    setShowFrameworkSelection(false);
    
    // Set the template name to the editable framework name
    setTemplateName(frameworkNames[frameworkIndex] || selectedFramework.name || '');
  };

  // Handle framework name change
  const handleFrameworkNameChange = (frameworkIndex, newName) => {
    setFrameworkNames(prev => ({
      ...prev,
      [frameworkIndex]: newName
    }));
  };

  // Handle framework deletion
  const handleDeleteFramework = (frameworkIndex) => {
    const updatedFrameworks = extractedFrameworks.filter((_, index) => index !== frameworkIndex);
    setExtractedFrameworks(updatedFrameworks);
    
    // Update framework names - reindex after deletion
    const updatedNames = {};
    updatedFrameworks.forEach((framework, newIndex) => {
      const oldIndex = extractedFrameworks.findIndex(f => f === framework);
      updatedNames[newIndex] = frameworkNames[oldIndex] || framework.name;
    });
    setFrameworkNames(updatedNames);

    // If no frameworks left, reset state
    if (updatedFrameworks.length === 0) {
      setShowFrameworkSelection(false);
      setCrawlSuccess(false);
      addNotification('All frameworks removed', 'info');
    } else if (updatedFrameworks.length === 1) {
      // If only one framework left, auto-select it
      handleFrameworkSelect(0, updatedFrameworks);
      addNotification('Framework selected automatically', 'success');
    } else {
      addNotification('Framework removed', 'success');
    }
  };

  // Toggle framework preview expansion
  const toggleFrameworkPreview = (frameworkIndex) => {
    setExpandedFrameworkPreviews(prev => ({
      ...prev,
      [frameworkIndex]: !prev[frameworkIndex]
    }));
  };

  // Switch to preview a framework (without permanent selection)
  const handleSwitchToFramework = (frameworkIndex, frameworksArray = null) => {
    // Use provided frameworks array or fall back to state
    const frameworks = frameworksArray || extractedFrameworks;
    const framework = frameworks[frameworkIndex];
    if (!framework) return;

    // Transform the learning goals for preview
    const formattedLearningGoals = framework.learningGoals.map((lg, index) => ({
      id: lg.id || `LG${index + 1}`,
      type: lg.type || 'main',
      description: lg.description
    }));

    // Update the form with this framework's data
    setLearningGoals(formattedLearningGoals);
    setTemplateName(frameworkNames[frameworkIndex] || framework.name || '');
    setCurrentPreviewFramework(frameworkIndex);
    setIsFrameworkSwitchingMode(true);
    
    // Check if this framework has already been created
    const isAlreadyCreated = createdFrameworks.has(frameworkIndex);
    setIsUrlFrameworkCreated(isAlreadyCreated);
    
    addNotification(`Switched to preview: ${frameworkNames[frameworkIndex] || framework.name}`, 'info');
  };

  // Confirm the currently previewed framework as final selection
  const handleConfirmFrameworkChoice = () => {
    if (currentPreviewFramework === null) return;
    
    // This is the final selection
    setSelectedFramework(currentPreviewFramework);
    setShowFrameworkSelection(false);
    setIsFrameworkSwitchingMode(false);
    setCrawlSuccess(true);
    
    addNotification('Framework selection confirmed!', 'success');
  };

  // Cancel framework switching and go back to selection
  const handleCancelFrameworkSwitching = () => {
    setIsFrameworkSwitchingMode(false);
    setCurrentPreviewFramework(null);
    setShowFrameworkSelection(true);
    
    // Reset to default form state
    setLearningGoals([{ id: 'LG1', type: 'main', description: '' }]);
    setTemplateName('');
    
    addNotification('Returned to framework selection', 'info');
  };

  // Add handler for method change to reset form state
  const handleCreateMethodChange = (method) => {
    setCreateMethod(method);
    
    // Reset form state when switching methods
    if (method === 'manual') {
      setExcelFile(null);
      setExcelData([]);
      setShowExcelPreview(false);
      resetCrawlState();
      // Only reset learning goals if we're not coming from a confirmed Excel upload
      if (excelData.length === 0 && !crawlSuccess) {
        setLearningGoals([{ id: 'LG1', type: 'main', description: '' }]);
        setTemplateName('');
      }
      setIsUrlFrameworkCreated(false);
      setCreatedFrameworks(new Set());
    } else if (method === 'url') {
      setExcelFile(null);
      setExcelData([]);
      setShowExcelPreview(false);
      setIsUrlFrameworkCreated(false);
      setCreatedFrameworks(new Set());
      // Reset learning goals when switching to URL crawling
      if (!crawlSuccess) {
        setLearningGoals([{ id: 'LG1', type: 'main', description: '' }]);
        setTemplateName('');
      }
    } else if (method === 'excel') {
      resetCrawlState();
      setIsUrlFrameworkCreated(false);
      setCreatedFrameworks(new Set());
    }
  };

  return (
    <div className="accreditation-bank">
      <div className="header">
        <h1>Accreditation Bank</h1>
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'view' ? 'active' : ''}`}
            onClick={() => setActiveTab('view')}
          >
            View Frameworks
          </button>
          <button 
            className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            Create New Framework
          </button>
        </div>
      </div>

      {activeTab === 'view' && (
        <div className="templates-container">
          <div className="templates-header">
            <h2>Available Frameworks</h2>
            <div className="templates-controls">
              {templates.length > 0 && !loading && !error && (
                <button 
                  className="expand-all-button" 
                  onClick={toggleAllTemplates}
                >
                  {allExpanded ? "Collapse All" : "Expand All"}
                </button>
              )}
            </div>
          </div>
          
          {loading && <div className="loading">Loading templates...</div>}
          
          {error && !loading && (
            <div className="error-message">
              Error: {error}
              <button onClick={fetchTemplates} className="retry-button">Retry</button>
            </div>
          )}
          
          {!loading && !error && templates.length === 0 && (
            <div className="no-templates">
              No templates found. Upload a new template to get started.
            </div>
          )}
          
          {!loading && !error && templates.map((template, index) => {
            const isExpanded = expandedTemplates[template.id];
            
            return (
              <div key={index} className={`template-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div 
                  className="template-header"
                  onClick={() => toggleTemplate(template.id)}
                >
                  <div className="expand-indicator-1" aria-label={isExpanded ? "Collapse" : "Expand"}>
                    {isExpanded ? 
                      <img src="/collapse-fullscreen-solid-svgrepo-com.svg" alt="Collapse template" className="expand-collapse-icon" /> : 
                      <img src="/expand-alt-svgrepo-com.svg" alt="Expand template" className="expand-collapse-icon" />
                    }
                  </div>
                  <div className="template-content">
                    <div className="template-name-section">
                      {editingTemplate === template.id ? (
                        <div className="edit-template-name" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={newTemplateName}
                            onChange={(e) => setNewTemplateName(e.target.value)}
                            placeholder="Enter new name"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="edit-actions">
                            <button 
                              className="save-button"
                              onClick={(e) => saveTemplateName(e, template.id)}
                              disabled={isUpdatingName}
                            >
                              {isUpdatingName ? 'Saving...' : 'Save'}
                            </button>
                            <button 
                              className="cancel-button"
                              onClick={cancelEditingTemplateName}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="template-name-display">
                          <h3>{template.name}</h3>
                          <button 
                            className="edit-name-button"
                            onClick={(e) => startEditingTemplateName(e, template)}
                            aria-label="Edit framework name"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="template-controls">
                        <button 
                        className={`generate-button ${generatingTemplates[template.id] ? 'generating' : ''} ${hasGeneratedContent(template) ? 'generated' : ''}`}
                        onClick={(e) => handleGenerateKeywords(e, template.id, school)}
                        disabled={generatingTemplates[template.id] || hasGeneratedContent(template)}
                        aria-label={hasGeneratedContent(template) ? "Keywords and concepts already generated" : "Generate keywords and concepts"}
                        title={hasGeneratedContent(template) ? "Keywords and concepts already generated" : "Generate new keywords and concepts"}
                      >
                        {generatingTemplates[template.id] ? (
                          <div className="spinner"></div>
                        ) : hasGeneratedContent(template) ? (
                          "Generated"
                        ) : (
                          "Generate"
                        )}
                      </button>
                      <button 
                        className="delete-button"
                        onClick={(e) => handleDeleteTemplate(e, template.id)}
                        aria-label="Delete template"
                      >
                        <img src="/icons8-trash.svg" alt="Delete template" className="trash-icon" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="learning-goals-list-1">
                  {template.LG && template.LG.map((lg, lgIndex) => {
                    const lgKey = `${template.id}-${lgIndex}`;
                    const isLGExpanded = expandedLGs[lgKey];
                    const isEditingThisLG = editingLG && editingLG.templateId === template.id && editingLG.lgIndex === lgIndex && !editingLG.isSubLG;
                    
                    return (
                    <div key={lgIndex} className="learning-goal-item-1">
                      <div className={`lg-header-container ${isLGExpanded ? 'expanded' : ''}`} onClick={() => toggleLG(template.id, lgIndex)}>
                        <h4>{lg.title}</h4>
                        <div className="expand-indicator-2">
                          <svg className="chevron-icon" viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                      </div>
                      
                      {isEditingThisLG ? (
                        <div className="edit-lg-content" onClick={(e) => e.stopPropagation()}>
                          <div className="edit-lg-field">
                            <label>Description:</label>
                            <textarea
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              rows={3}
                              placeholder="Enter description"
                            />
                          </div>
                          
                          <div className="edit-lg-field">
                            <label>Keywords:</label>
                            <textarea
                              value={editKeywords}
                              onChange={(e) => setEditKeywords(e.target.value)}
                              rows={2}
                              placeholder="Enter keywords separated by commas"
                            />
                            <div className="edit-keywords-grid">
                              {keywordBlocks.map((keyword, idx) => (
                                <div key={idx} className="keyword-block">
                                  <input
                                    type="text"
                                    value={keyword}
                                    onChange={(e) => updateKeywordBlock(idx, e.target.value)}
                                    placeholder="Keyword"
                                  />
                                  <button 
                                    type="button" 
                                    className="remove-keyword" 
                                    onClick={() => removeKeywordBlock(idx)}
                                    aria-label="Remove keyword"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                              <div className="add-keyword-block" onClick={addKeywordBlock}>
                                <span>+ Add Keyword</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="edit-lg-field">
                            <label>Concepts:</label>
                            <textarea
                              value={editConcepts}
                              onChange={(e) => setEditConcepts(e.target.value)}
                              rows={3}
                              placeholder="Enter concepts, one per line"
                            />
                            <div className="concepts-container">
                              {conceptBoxes.map((concept, idx) => (
                                <div key={idx} className="concept-box">
                                  <textarea
                                    value={concept}
                                    onChange={(e) => updateConceptBox(idx, e.target.value)}
                                    placeholder="Enter concept"
                                  />
                                  <button 
                                    type="button" 
                                    className="remove-concept" 
                                    onClick={() => removeConceptBox(idx)}
                                    aria-label="Remove concept"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                              <div className="add-concept-box" onClick={addConceptBox}>
                                <span>+ Add Concept</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="edit-lg-actions">
                            <button 
                              className="save-button"
                              onClick={saveLGContent}
                              disabled={isUpdatingContent}
                            >
                              {isUpdatingContent ? 'Saving...' : 'Save'}
                            </button>
                            <button 
                              className="cancel-button"
                              onClick={cancelEditingLG}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="lg-description-container">
                            <p>{lg.description}</p>
                            <button 
                              className="edit-content-button"
                              onClick={(e) => startEditingLG(e, template.id, lgIndex)}
                              aria-label="Edit learning goal"
                            >
                              Edit
                            </button>
                          </div>
                      
                          {/* Keywords Section - Only show when expanded */}
                          {isLGExpanded && lg.keywords && lg.keywords.length > 0 && (
                            <div className="lg-keywords-section-1">
                              <div className="keywords-header">
                                <h5>Keywords</h5>
                              </div>
                              <div className="lg-keywords-container-1">
                                {lg.keywords.map((keyword, idx) => (
                                  <span key={idx} className="lg-keyword-tag-1">
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Concepts Section - Only show when expanded */}
                          {isLGExpanded && lg.concepts && lg.concepts.length > 0 && (
                            <div className="lg-concepts-section-1">
                              <div className="concepts-header">
                                <h5>Concepts</h5>
                              </div>
                              <div className="lg-concepts-list-1">
                                {lg.concepts.map((concept, idx) => (
                                  <p key={idx} className="lg-concept-item-1">
                                    {concept}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {isLGExpanded && lg.objectives && lg.objectives.length > 0 && (
                        <div className="sub-goals">
                          <h5>Sub-goals:</h5>
                          <ul>
                            {lg.objectives.map((obj, objIndex) => {
                              const subLgKey = `${template.id}-${lgIndex}-${objIndex}`;
                              const isSubLGExpanded = expandedSubLGs[subLgKey];
                              const isEditingThisSubLG = editingLG && 
                                editingLG.templateId === template.id && 
                                editingLG.lgIndex === lgIndex && 
                                editingLG.isSubLG && 
                                editingLG.subIndex === objIndex;
                              
                              return (
                              <li key={objIndex} className="sub-goal-item">
                                <div className="sub-goal-header">
                                  <div className="sub-goal-title">
                                    <strong>{obj.sub_learning_goal_id}</strong>: {!isEditingThisSubLG && obj.description}
                                  </div>
                                  {!isEditingThisSubLG && (
                                    <button 
                                      className="edit-content-button"
                                      onClick={(e) => startEditingLG(e, template.id, lgIndex, true, objIndex)}
                                      aria-label="Edit sub-learning goal"
                                    >
                                      Edit
                                    </button>
                                  )}
                                </div>
                                
                                {isEditingThisSubLG ? (
                                  <div className="edit-lg-content" onClick={(e) => e.stopPropagation()}>
                                    <div className="edit-lg-field">
                                      <label>Description:</label>
                                      <textarea
                                        value={editDescription}
                                        onChange={(e) => setEditDescription(e.target.value)}
                                        rows={3}
                                        placeholder="Enter description"
                                      />
                                    </div>
                                    
                                    <div className="edit-lg-field">
                                      <label>Keywords:</label>
                                      <textarea
                                        value={editKeywords}
                                        onChange={(e) => setEditKeywords(e.target.value)}
                                        rows={2}
                                        placeholder="Enter keywords separated by commas"
                                      />
                                      <div className="edit-keywords-grid">
                                        {keywordBlocks.map((keyword, idx) => (
                                          <div key={idx} className="keyword-block">
                                            <input
                                              type="text"
                                              value={keyword}
                                              onChange={(e) => updateKeywordBlock(idx, e.target.value)}
                                              placeholder="Keyword"
                                            />
                                            <button 
                                              type="button" 
                                              className="remove-keyword" 
                                              onClick={() => removeKeywordBlock(idx)}
                                              aria-label="Remove keyword"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                        <div className="add-keyword-block" onClick={addKeywordBlock}>
                                          <span>+ Add Keyword</span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="edit-lg-field">
                                      <label>Concepts:</label>
                                      <textarea
                                        value={editConcepts}
                                        onChange={(e) => setEditConcepts(e.target.value)}
                                        rows={3}
                                        placeholder="Enter concepts, one per line"
                                      />
                                      <div className="concepts-container">
                                        {conceptBoxes.map((concept, idx) => (
                                          <div key={idx} className="concept-box">
                                            <textarea
                                              value={concept}
                                              onChange={(e) => updateConceptBox(idx, e.target.value)}
                                              placeholder="Enter concept"
                                            />
                                            <button 
                                              type="button" 
                                              className="remove-concept" 
                                              onClick={() => removeConceptBox(idx)}
                                              aria-label="Remove concept"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                        <div className="add-concept-box" onClick={addConceptBox}>
                                          <span>+ Add Concept</span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="edit-lg-actions">
                                      <button 
                                        className="save-button"
                                        onClick={saveLGContent}
                                        disabled={isUpdatingContent}
                                      >
                                        {isUpdatingContent ? 'Saving...' : 'Save'}
                                      </button>
                                      <button 
                                        className="cancel-button"
                                        onClick={cancelEditingLG}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {/* Sub LG Keywords - Always show all */}
                                    {obj.keywords && obj.keywords.length > 0 && (
                                      <div className="sub-keywords-section">
                                        <div className="sub-keywords-label-container">
                                          <span className="sub-keywords-label">Keywords: </span>
                                        </div>
                                        <div className="sub-keywords-container">
                                          {obj.keywords.map((kw, kidx) => (
                                            <span key={kidx} className="sub-keyword-tag">
                                              {kw}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Sub LG Concepts - Always show all */}
                                    {obj.concepts && obj.concepts.length > 0 && (
                                      <div className="sub-concepts-section">
                                        <div className="sub-concepts-label-container">
                                          <span className="sub-concepts-label">Concepts: </span>
                                        </div>
                                        <div className="sub-concepts-container">
                                          {obj.concepts.map((concept, cidx) => (
                                            <p key={cidx} className="sub-concept-item">
                                              {concept}
                                            </p>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'upload' && (
        <div className="upload-container">
          
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}
          
          {error && (
            <div className="error-message">Error: {error}</div>
          )}
          
          {/* Creation Method Selection */}
          <div className="creation-methods">
            <h3>Choose Creation Method:</h3>
            <div className="method-options">
              <label className={`method-option ${createMethod === 'manual' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="createMethod"
                  value="manual"
                  checked={createMethod === 'manual'}
                  onChange={(e) => handleCreateMethodChange(e.target.value)}
                />
                <div className="method-content">
                  <h4>Manual Entry</h4>
                  <p>Enter learning goals manually one by one</p>
                </div>
              </label>

              <label className={`method-option ${createMethod === 'excel' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="createMethod"
                  value="excel"
                  checked={createMethod === 'excel'}
                  onChange={(e) => handleCreateMethodChange(e.target.value)}
                />
                <div className="method-content">
                  <h4>Excel Upload</h4>
                  <p>Download template, fill it out, and upload</p>
                </div>
              </label>

              <label className={`method-option ${createMethod === 'url' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="createMethod"
                  value="url"
                  checked={createMethod === 'url'}
                  onChange={(e) => handleCreateMethodChange(e.target.value)}
                />
                <div className="method-content">
                  <h4>URL</h4>
                  <p>Read learning goals from a URL</p>
                </div>
              </label>
            </div>
          </div>

          {/* Excel Upload Section */}
          {createMethod === 'excel' && !showExcelPreview && (
            <div className="excel-upload-section">
              <div className="excel-instructions">
                <h4>Excel Upload Instructions:</h4>
                <ol>
                  <li>Download the Excel template below</li>
                  <li>Read the instructions in the Excel file and fill in the framework accordingly</li>
                  <li>Save the file and upload it back here</li>
                </ol>
              </div>

              <div className="excel-download">
                <button 
                  type="button"
                  className="download-template-button"
                  onClick={handleDownloadTemplate}
                >
                  📥 Download Excel Template
                </button>
              </div>

              <div className="excel-upload">
                <label htmlFor="excel-file" className="excel-upload-label">
                  Upload Completed Excel:
                </label>
                <div 
                  className={`excel-file-drop-zone ${isDragOver ? 'drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('excel-file').click()}
                >
                  <div className="drop-zone-content">
                    <div className="upload-icon">📁</div>
                    <p className="drop-zone-text">
                      <strong>Drag and drop your Excel file here</strong>
                      <br />
                      or click to browse files
                    </p>
                    <p className="file-info">Accepts .xlsx files only</p>
                  </div>
                  <input
                    id="excel-file"
                    type="file"
                    accept=".xlsx"
                    onChange={handleExcelFileChange}
                    className="excel-file-input-hidden"
                    style={{ display: 'none' }}
                  />
                </div>
                {excelFile && (
                  <div className="selected-file-info">
                    <span>📄 Selected file: {excelFile.name}</span>
                  </div>
                )}
                {isParsingExcel && (
                  <div className="parsing-indicator">
                    <div className="spinner" aria-label="Loading"></div>
                    <span>Parsing Excel file...</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Excel Preview Section */}
          {showExcelPreview && excelData.length > 0 && (
            <div className="excel-preview-section">
              <h4>🔍 Preview Excel Data</h4>
              <p className="excel-preview-instructions">
                Review and edit your framework below. 
              </p>
              
              {excelData.map((lg, index) => (
                <div 
                  key={lg.id} 
                  className={`form-group learning-goal-input ${lg.type === 'main' ? 'main' : 'sub'}`}
                >
                  <label className="lg-header">
                    <span className={`lg-badge ${lg.type}`}>{lg.id}</span>
                    {lg.type === 'main' ? 'Learning Goal' : 'Sub-Learning Goal'}
                    <button 
                      type="button" 
                      className="remove-button"
                      onClick={() => handleRemoveExcelLearningGoal(index)}
                      title={`Remove ${lg.id}`}
                    >
                      Remove
                    </button>
                  </label>
                  <textarea 
                    value={lg.description}
                    onChange={(e) => handleExcelLearningGoalChange(index, e.target.value)}
                    placeholder={`Enter description for ${lg.id}...`}
                    rows="3"
                  />
                  
                  {lg.type === 'main' && (
                    <button 
                      type="button" 
                      className="add-sub-lg-button"
                      onClick={() => handleAddExcelSubLearningGoal(index)}
                    >
                      + Add Sub-Learning Goal to {lg.id}
                    </button>
                  )}
                </div>
              ))}
              
              <div className="add-main-lg-section">
                <button 
                  type="button" 
                  className="add-lg-button"
                  onClick={() => handleAddExcelLearningGoal('main')}
                >
                  + Add Main Learning Goal
                </button>
              </div>
              
              <div className="excel-preview-actions">
                <button 
                  type="button" 
                  className="confirm-excel-button"
                  onClick={handleConfirmExcelData}
                >
                  ✓ Confirm
                </button>
                <button 
                  type="button" 
                  className="cancel-excel-button"
                  onClick={handleCancelExcelUpload}
                >
                  ✕ Cancel
                </button>
              </div>
            </div>
          )}

          {/* URL Crawling Section */}
          {createMethod === 'url' && (
            <div className="url-crawling-section">
              <div className="url-instructions">
                <h4>Instructions:</h4>
                <ol>
                  <li>Enter a URL of a page containing accreditation framework information</li>
                  <li>Our AI will read the page and extract learning goals automatically</li>
                  <li>Review and switch between frameworks if multiple are found</li>
                  <li>Edit the framework and confirm to create</li>
                </ol>
              </div>

              <div className="url-input-section">
                <label htmlFor="crawl-url">Website URL:</label>
                <div className="url-input-group">
                  <input
                    id="crawl-url"
                    type="url"
                    value={crawlUrl}
                    onChange={(e) => setCrawlUrl(e.target.value)}
                    placeholder="https://example.com/accreditation-framework"
                    required
                    disabled={isCrawling}
                  />
                  <button
                    type="button"
                    className="crawl-button"
                    onClick={handleUrlCrawl}
                    disabled={!crawlUrl.trim() || isCrawling}
                  >
                    {isCrawling ? 'Extracting...' : 'Extract Frameworks'}
                  </button>
                </div>
              </div>

              {isCrawling && (
                <div className="crawling-progress">
                  <div className="progress-indicator">
                    <div className="spinner" aria-label="Loading"></div>
                    <span>Reading website data and extracting frameworks</span>
                  </div>
                  <div className="progress-steps">
                    <div className="progress-step completed">
                      📄 Reading website data
                      <span className="progress-percentage">{Math.round(crawlProgressPercentage)}%</span>
                    </div>
                  </div>
                </div>
              )}

              {crawlError && (
                <div className="error-message">
                  <strong>Error:</strong> {crawlError}
                </div>
              )}

              {/* Framework Selection UI */}
              

              {crawlSuccess && !showFrameworkSelection && !isFrameworkSwitchingMode && (
                <div className="success-message">
                  Framework extracted successfully! You can now review and edit the learning goals below.
                </div>
              )}

              {/* Framework Switching Toolbar */}
              {isFrameworkSwitchingMode && (
                <div className="framework-switching-toolbar">
                  <div className="switching-header">

                    <p>Currently previewing: <strong>{frameworkNames[currentPreviewFramework] || extractedFrameworks[currentPreviewFramework]?.name}</strong></p>
                  </div>
                  
                  <div className="switching-controls">
                    <div className="framework-navigation">
                      {extractedFrameworks.map((framework, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`framework-nav-button ${index === currentPreviewFramework ? 'active' : ''} ${createdFrameworks.has(index) ? 'created' : ''}`}
                          onClick={() => handleSwitchToFramework(index)}
                          title={`${frameworkNames[index] || framework.name} ${createdFrameworks.has(index) ? '(Created)' : ''}`}
                        >
                          #{index + 1}
                          {createdFrameworks.has(index) && <span className="created-indicator">✓</span>}
                        </button>
                      ))}
                    </div>
                    
                    
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manual Entry Form (existing form) */}
          {(createMethod === 'manual' || (createMethod === 'excel' && excelData.length > 0 && !showExcelPreview) || (createMethod === 'url' && (crawlSuccess || isFrameworkSwitchingMode))) && (
          <form onSubmit={handleSubmit} className="template-form">
            <div className="form-group">
                <label htmlFor="template-name">Framework Name:</label>
              <input
                id="template-name"
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                required
                  placeholder="Enter framework name"
              />
            </div>
            
            <h3>Learning Goals</h3>
            
            {learningGoals.map((lg, index) => (
                <div 
                  key={index} 
                  className={`form-group learning-goal-input ${lg.type}`}
                >
                <div className="lg-header">
                  <label htmlFor={`lg-${index}`}>{lg.id}:</label>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => handleRemoveLearningGoal(index)}
                      disabled={index === 0 && learningGoals.length === 1 && !lg.description.trim()}
                  >
                    Remove
                  </button>
                </div>
                <textarea
                  id={`lg-${index}`}
                  value={lg.description}
                  onChange={(e) => handleLearningGoalChange(index, e.target.value)}
                  required
                  placeholder={`Enter description for ${lg.id}`}
                  rows={3}
                />
                  
                  {/* Show "Add Sub-Learning Goal" option for main learning goals by default */}
                  {lg.type === 'main' && (
                    <div className="add-sub-lg-option">
                      <button
                        type="button"
                        className="add-sub-lg-button"
                        onClick={() => handleAddSubLearningGoal(index)}
                      >
                        + Add Sub-Learning Goal to {lg.id}
                      </button>
                    </div>
                  )}
              </div>
            ))}
            
            <div className="add-buttons">
              <button 
                type="button" 
                className="add-button main"
                onClick={() => handleAddLearningGoal('main')}
              >
                + Add Main Learning Goal
              </button>
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className={`submit-button ${isUrlFrameworkCreated ? 'created' : ''}`}
                disabled={isSubmitting || isUrlFrameworkCreated}
              >
                {isSubmitting ? 'Creating...' : 
                 isUrlFrameworkCreated ? '✓ Created' : 
                 'Confirm'}
              </button>
            </div>
          </form>
          )}
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-header">
              <h2>Confirm Delete</h2>
              <button className="delete-close-button" onClick={cancelDelete}>×</button>
            </div>
            <div className="delete-modal-content">
              <div className="delete-warning-icon">⚠️</div>
              <p>Are you sure you want to delete this template?</p>
              <p className="delete-warning-text">This action cannot be undone.</p>
            </div>
            <div className="delete-modal-footer">
              <button className="delete-cancel-button" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="delete-confirm-button" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      <NotificationContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
    </div>
  );
};

export default AccreditationBank;